import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, ArrowRight, MessageCircle, IndianRupee, Maximize, Zap, Users, CalendarClock, Factory, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import SEOHead from './seo/SEOHead';
import Breadcrumbs from './seo/Breadcrumbs';
import InternalLinks from './seo/InternalLinks';
import GoogleReviewsStrip from './GoogleReviewsStrip';
import CapacityStrip from './CapacityStrip';
import { JsonLd, faqSchema } from './seo/schemas';
import { CAPACITY_HERO } from '../data/capacityPlants';

const CLIENT_SCOPE = [
  'Land and covered building/shed as per layout',
  'Civil work — flooring with slopes, drainage, partitioning',
  'Borewell / raw water source with storage tank',
  'Three-phase power connection at specified load',
  'Statutory license fees (BIS, FSSAI, pollution board)',
  'Plant manpower and packaging raw material'
];

const ALDEN_SCOPE = [
  'Plant layout design matched to your building & BIS norms',
  'Complete machinery manufacturing and factory testing',
  'RO treatment system engineered to your water report',
  'Delivery, erection, piping and electrical integration',
  'Commissioning, line balancing and trial production',
  'Operator training with documented SOPs',
  'BIS / FSSAI technical documentation support',
  'After-sales service, spares and AMC support PAN India'
];

const QuoteForm = ({ bpm }) => {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, projectType: `${bpm} Water Plant` })
      });
      if (!response.ok) throw new Error('Failed to submit');
      const result = await response.json();
      toast.success(result.message);
      setFormData({ name: '', phone: '' });
    } catch (error) {
      toast.error('Unable to submit. Please call +91 93512 92512');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="capacity-quote-form" className="bg-gradient-to-br from-alden-green to-alden-green-light rounded-2xl p-8 shadow-2xl">
      <h3 className="text-2xl font-bold text-white mb-2">Get {bpm} Project Report</h3>
      <p className="text-white/90 mb-6">Free quotation with machinery list, layout &amp; ROI worksheet</p>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="capacity-quote-form">
        <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Your Name *" className="h-12 bg-white text-base" data-testid="capacity-quote-name" />
        <Input required type="tel" pattern="[0-9+\-\s]{10,15}" value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="Phone / WhatsApp Number *" className="h-12 bg-white text-base" data-testid="capacity-quote-phone" />
        <Button type="submit" disabled={isSubmitting} className="w-full bg-white text-alden-green hover:bg-gray-100 h-14 text-lg font-bold" data-testid="capacity-quote-submit">
          {isSubmitting ? 'Submitting...' : 'Request Free Quotation'} <ArrowRight className="ml-2" />
        </Button>
      </form>
      <a href="tel:+919351292512" className="flex items-center justify-center gap-2 text-white mt-4 font-semibold hover:underline">
        <Phone className="w-5 h-5" /> Or Call: +91 93512 92512
      </a>
    </div>
  );
};

const CapacityPlantTemplate = ({ plant, path }) => {
  const stats = [
    { icon: IndianRupee, label: 'Machinery Investment', value: plant.investment },
    { icon: Factory, label: 'Production Capacity', value: `${plant.bph} (${plant.daily})` },
    { icon: Maximize, label: 'Space Requirement', value: plant.space },
    { icon: Zap, label: 'Power Requirement', value: plant.power },
    { icon: Users, label: 'Manpower', value: plant.manpower },
    { icon: CalendarClock, label: 'Installation Timeline', value: plant.timeline }
  ];

  return (
    <div className="min-h-screen" data-testid="capacity-plant-page">
      <SEOHead title={plant.metaTitle} description={plant.metaDescription} path={path} />
      <JsonLd data={faqSchema(plant.faqs)} />
      <Breadcrumbs items={[{ label: 'Beverage Plants', to: '/solutions/beverage-plants' }, { label: plant.h1 }]} />

      {/* Hero */}
      <section className="relative bg-alden-navy text-white overflow-hidden">
        <img src={CAPACITY_HERO} alt={`${plant.h1} turnkey installation by Alden Technologies`} className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative container mx-auto px-4 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-block bg-alden-green px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                Complete Turnkey Project — {plant.bph}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">{plant.h1}</h1>
              <p className="text-lg text-white/85 mb-8">{plant.sub}</p>
              <div className="flex flex-wrap gap-3">
                <Button className="bg-alden-green hover:bg-alden-green-light text-white py-6 px-7 text-base font-semibold"
                  onClick={() => document.getElementById('capacity-quote-form')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="capacity-hero-quote-cta">
                  Get Project Quotation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <a href="tel:+919351292512">
                  <Button className="bg-white text-alden-navy hover:bg-gray-100 py-6 px-7 text-base font-semibold">
                    <Phone className="mr-2 w-4 h-4" /> Talk to an Engineer
                  </Button>
                </a>
                <a href={`https://wa.me/919351292512?text=${encodeURIComponent(`Hi, I need a quotation for a ${plant.bpm} mineral water plant`)}`}
                  target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white py-6 px-7 text-base font-semibold">
                    <MessageCircle className="mr-2 w-4 h-4" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
            <QuoteForm bpm={plant.bpm} />
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Plant Configuration at a Glance</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="capacity-stats-grid">
            {stats.map((s) => (
              <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center hover:border-alden-green transition-colors">
                <s.icon className="w-7 h-7 text-alden-green mx-auto mb-3" />
                <div className="text-sm font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">
            Figures are indicative ranges based on typical configurations — final specifications depend on your site, water report and product mix. No unrealistic guarantees, ever.
          </p>
        </div>
      </section>

      {/* Machinery Included + Optional */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Machinery Included</h2>
            <ul className="space-y-3" data-testid="machinery-included-list">
              {plant.machinery.map((m) => (
                <li key={m} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-alden-green flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">{m}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Optional Equipment</h2>
            <ul className="space-y-3" data-testid="optional-equipment-list">
              {plant.optional.map((m) => (
                <li key={m} className="flex items-start gap-3 bg-white border border-dashed border-alden-green/40 rounded-lg p-4">
                  <PlusCircle className="w-5 h-5 text-alden-green flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">{m}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 bg-alden-green/5 border-l-4 border-alden-green p-6 rounded-r-xl">
              <p className="text-gray-800">
                <strong className="text-alden-green">Not sure what to include?</strong> Our engineers configure the plant
                around your budget, market and building — talk to us before finalising anything.
              </p>
              <a href="tel:+919351292512" className="inline-flex items-center gap-2 text-alden-green font-bold mt-3 hover:underline">
                <Phone className="w-4 h-4" /> +91 93512 92512
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scope Table */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Scope of Work — Who Does What</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-testid="scope-table">
            <div className="border-2 border-alden-green rounded-2xl overflow-hidden">
              <div className="bg-alden-green text-white px-6 py-4 font-bold text-lg">Alden Scope (Turnkey)</div>
              <ul className="p-6 space-y-3">
                {ALDEN_SCOPE.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-alden-green flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-2 border-gray-300 rounded-2xl overflow-hidden">
              <div className="bg-alden-navy text-white px-6 py-4 font-bold text-lg">Client Scope</div>
              <ul className="p-6 space-y-3">
                {CLIENT_SCOPE.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{plant.bpm} Plant — Frequently Asked Questions</h2>
          <div className="space-y-5">
            {plant.faqs.map((f) => (
              <Card key={f.q} className="border-2 border-gray-200 hover:border-alden-green transition-all bg-white">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{f.q}</h3>
                  <p className="text-gray-700">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/blog/mineral-water-plant-cost-india" className="text-alden-green font-semibold hover:underline inline-flex items-center gap-2">
              Read the complete Mineral Water Plant Cost Guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <GoogleReviewsStrip />
      <CapacityStrip title="Compare Other Plant Capacities" />
      <InternalLinks currentPath={path} />
    </div>
  );
};

export default CapacityPlantTemplate;
